import { useEffect, useRef } from 'react';
import {
  Modal,
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  theme,
  type Message,
} from '@agentisai/design-system';
import { AttachFileButton } from './AttachFileButton';
import { AttachmentChips } from './AttachmentChips';
import { AttachErrorAlert } from './AttachErrorAlert';
import { OptionChips } from './OptionChips';
import type { ParsedOption } from '../lib/parseOptions';
import { composerChrome } from './composerChrome.css';
import { CreateProgress, type CreateStage } from './CreateProgress';
import { TurnActivity } from './TurnActivity';
import type { TurnEvent } from '@agentisai/shared-types';
import {
  buttonRow,
  confirmRow,
  dialog as dialogCls,
  dialogChatPanel,
  entryChipRow,
} from './CreateWorkstreamPanel.css';

/**
 * ADR-0059 Phase 1 (Orient). Kept WORD-FOR-WORD identical to the agent skill's
 * Phase-1 opening so the seeded bubble and the model's own first turn cannot
 * drift apart (a unit test asserts the exact string). The three ways in are
 * affordances, not narration — they are ENTRY_CHIPS below.
 *
 * Exported because it is a real agent turn, not decoration: the parent prepends
 * it to every pre-create `messages[]` that goes on the wire, so the model has a
 * record of having asked it. One constant, both consumers — a second copy would
 * let the rendered bubble and the wire text drift.
 */
export const CREATE_INTRO_TEXT = 'What kind of work are you looking to get done?';

const CW_AGENT_INTRO: Message = {
  id: 'cw-agent-1',
  memberId: 'workstream-agent',
  memberName: 'Discovery',
  memberRole: '',
  memberType: 'agent',
  variant: 'mira',
  text: CREATE_INTRO_TEXT,
  time: 'Just now',
};

/**
 * The three ways into the interview (ADR-0059 Phase 1). Two prefill the composer
 * with a stem the user finishes; the third hands off to the paperclip's picker.
 * Prefill, never send — firing a turn on click would post a half-sentence in
 * the user's name.
 */
const ENTRY_CHIPS: readonly { label: string; icon: typeof icons.Lightbulb; prefill?: string }[] = [
  { label: 'Describe an outcome', icon: icons.Lightbulb, prefill: "The outcome I'm after is " },
  { label: 'Show me your process', icon: icons.Workflow, prefill: 'Today, the way this works is ' },
  // No `prefill` — this one opens the paperclip's picker, so it renders only
  // when that picker exists (`onFilesPicked`). Without the guard it is an
  // affordance that silently does nothing: `openFilePicker` reaches for an
  // input that AttachFileButton never rendered.
  { label: 'Upload an example', icon: icons.Paperclip },
];

export interface CreateWorkstreamPanelProps {
  open: boolean;
  onClose: () => void;
  /** Conversation transcript (DS Message objects). Rendered after the seeded intro. */
  messages?: Message[];
  /**
   * Preformatted display time for the seeded agent intro bubble (the parent
   * stamps it at panel-open and formats it through the same formatter as the
   * transcript, so it ages consistently). Defaults to `'Just now'`.
   */
  introTime?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  /**
   * Max characters the composer value may have (the shared `MAX_TEXT_LEN`
   * contract limit). When `value` exceeds it, an inline over-limit message shows
   * — so the user gets feedback BEFORE sending, not a post-send server rejection.
   * The parent drives `sendDisabled` from the same check. Omit to skip it.
   */
  maxLength?: number;
  /** In-flight chat-turn indicator — disables send while true. */
  pending?: boolean;
  /**
   * The in-flight turn's checkpoint trail (ADR-0042), narrated under the typing
   * indicator while `pending` — the pre-create poll carries the same events as a
   * post-create turn (ADR-0074).
   */
  turnEvents?: readonly TurnEvent[];
  onCreate?: () => void;
  createDisabled?: boolean;
  creating?: boolean;
  /** Error message from the send-chat-turn mutation (rendered as a live region). */
  sendError?: string | null;
  /** Error message from the create-workstream mutation (rendered as a live region). */
  createError?: string | null;
  /**
   * Files staged for this workstream-to-be. They cannot upload yet — the upload
   * routes are gated by `resolveOwned`, which 404s until the workstream row
   * exists — so the parent holds them and flushes them the moment auto-create
   * (ADR-0044) mints the id, after which they appear in the Files rail.
   */
  stagedFiles?: readonly File[];
  /** Files were picked. Validation + staging are the parent's job. */
  onFilesPicked?: (files: File[]) => void;
  /** Drop one staged file. */
  onRemoveFile?: (file: File) => void;
  /** Name of the file uploading during the post-create flush, or `null`. */
  uploadingFilename?: string | null;
  /** Upload progress 0–100 for {@link uploadingFilename}. */
  uploadProgress?: number;
  /** Attach validation / upload failure copy, already user-facing. */
  attachError?: string | null;
  /** Clear {@link attachError}. */
  onDismissAttachError: () => void;
  /**
   * How far the consultation has got. Derived by the parent from the two wire
   * signals (`workstreamTitle` held, `designConfirmed` latched) — never
   * recomputed here, so no two affordances can disagree.
   */
  stage?: CreateStage;
  /** Agent-proposed title, once held — shown on the breadcrumb's Design step. */
  workstreamTitle?: string | null;
  /** Show the one-click design approval. The visibility condition is the parent's. */
  showConfirmDesign?: boolean;
  /** Approve the design. Sends an ordinary user turn through `onSend`'s path. */
  onConfirmDesign?: () => void;
  /** Numbered options the agent just offered, as one-click answers (via `onSend`). */
  options?: readonly ParsedOption[];
}

/**
 * CreateWorkstreamPanel — one-off page composition for the "Create new
 * workstream" surface. Composes DS Modal (focus trap, Escape, backdrop
 * dismiss) + ChatPanel fluid + MessageBubble (seeded agent intro + transcript)
 * + Composer (empty-tenant placeholder) + Create affordance.
 *
 * State is fully owned by the parent (HomePage). This component renders only —
 * `stage`, `showConfirmDesign` and `createDisabled` are all derived there from
 * the same two wire signals, so the ADR-0059 affordances cannot disagree.
 */
export function CreateWorkstreamPanel({
  open,
  onClose,
  messages = [],
  introTime = 'Just now',
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  turnEvents = [],
  onCreate,
  createDisabled = true,
  creating = false,
  sendError,
  createError,
  stagedFiles = [],
  onFilesPicked,
  onRemoveFile,
  uploadingFilename = null,
  uploadProgress = 0,
  attachError,
  onDismissAttachError,
  stage = 'understanding',
  workstreamTitle,
  showConfirmDesign = false,
  onConfirmDesign,
  options = [],
}: CreateWorkstreamPanelProps) {
  // Auto-scroll the message list to the bottom whenever a new chat turn lands
  // (the server reply, the user echo, or the create confirmation) — or when
  // the awaiting-reply typing indicator appears — so the latest content is
  // visible. A sentinel at the end of the messages slot locates the
  // ChatPanel's `overflow-y:auto` message list, which is then pinned to its
  // full scroll height (past the list's bottom padding + the typing-indicator
  // lozenge, so the newest item sits flush at the bottom). Imperative DOM
  // sync — the sanctioned use of an effect + ref (frontend-architecture
  // R24/R25), not data-in-effect.
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length, pending]);

  // One ref onto the Composer root serves both entry-chip gestures: the Lexical
  // contenteditable (prefill focus) and AttachFileButton's hidden file input
  // (the upload chip) — reused rather than adding a second input, which would
  // fork the accept list. Sanctioned imperative DOM (frontend-architecture R25).
  const composerRef = useRef<HTMLDivElement | null>(null);
  const focusComposer = () =>
    composerRef.current?.querySelector<HTMLElement>('[contenteditable="true"]')?.focus();
  // Empty composer only — the stem must never overwrite a half-typed reply.
  // With a draft present the chip is just a focus gesture.
  const prefillComposer = (text: string) => {
    if (!value) onChange?.(text);
    focusComposer();
  };
  const openFilePicker = () =>
    composerRef.current?.querySelector<HTMLInputElement>('input[type="file"]')?.click();

  /** The entry points are for getting IN — they retire once a turn has landed. */
  const showEntryChips = messages.length === 0;

  // The paperclip stages files rather than uploading them: there is no
  // workstream row to own an artifact yet (the pre-create draft seam, ADR-0047),
  // so the parent holds the picked Files and flushes them into the ledger the
  // instant auto-create mints the id. Nothing is claimed that cannot be
  // delivered — the X removes a file nothing has stored.
  const composerLeadingActions = (
    <>
      {onFilesPicked && (
        <AttachFileButton onFilesPicked={onFilesPicked} disabled={uploadingFilename !== null} />
      )}
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create New Workstream"
      size="lg"
      flushBody
      className={dialogCls}
    >
      <ChatPanel
        fluid
        className={dialogChatPanel}
        // The breadcrumb goes in the CAPTION slot, not the title: the Modal
        // already titles this dialog ("Create New Workstream") and a second
        // heading-weight element beneath it would compete with it. ChatPanel
        // renders its header band on caption alone.
        caption={<CreateProgress stage={stage} title={workstreamTitle} />}
        awaitingReply={pending}
        // The pre-create turn is `forcedAgents: ['discovery']` in the BFF, so
        // the answering agent is known here — no reason to say "Workstream
        // Agent is typing" when discovery is the only agent that can reply.
        typingLabel="Discovery is typing"
        messages={
          <>
            <MessageBubble
              key={CW_AGENT_INTRO.id}
              message={{ ...CW_AGENT_INTRO, time: introTime }}
            />
            {messages.map((m, i) => (
              // 'user' is HomePage's memberId for the signed-in user's turns.
              // Same grouping as WorkspaceConversation; the intro is the row before [0].
              <MessageBubble
                key={m.id}
                message={m}
                isSelf={m.memberId === 'user'}
                showMeta={(messages[i - 1] ?? CW_AGENT_INTRO).memberName !== m.memberName}
              />
            ))}
            {pending && <TurnActivity events={turnEvents} />}
            {/* Scroll anchor — kept in view as new turns arrive. */}
            <div ref={bottomRef} aria-hidden="true" />
          </>
        }
        composer={
          <>
            {/* Same band, same rhythm as the workstream conversation. Rendered
                only when it has content: unlike there, every block here is
                conditional, so an always-present wrapper would leave a stray gap
                above the Composer on the common empty case. */}
            {(sendError ||
              stagedFiles.length > 0 ||
              showConfirmDesign ||
              showEntryChips ||
              options.length > 0) && (
              <div className={composerChrome}>
                {/* Composer chrome, not a chat turn: they fill the input, so
                    they sit against it. Retired once a turn has landed. */}
                {showEntryChips && (
                  <div className={entryChipRow}>
                    {ENTRY_CHIPS.filter((chip) => chip.prefill || onFilesPicked).map((chip) => (
                      <Button
                        key={chip.label}
                        size="sm"
                        variant="ghostStrong"
                        leftIcon={<Icon as={chip.icon} size={14} />}
                        onClick={() =>
                          chip.prefill ? prefillComposer(chip.prefill) : openFilePicker()
                        }
                      >
                        {chip.label}
                      </Button>
                    ))}
                  </div>
                )}
                {onSend && <OptionChips options={options} onPick={onSend} />}
                {showConfirmDesign && (
                  // One-click approval for the design summary (ADR-0059). Sends
                  // an ORDINARY user turn — the model latches `confirmed` as it
                  // would for a typed yes. No "Adjust" twin: the composer is
                  // the adjust affordance.
                  //
                  // Visibility is the PARENT's call, and is no longer keyed on
                  // the title alone: a title arrives by the agent's first or
                  // second reply, so that gate showed this at turn 1 — where
                  // clicking it sends a confirmation the model latches, minting
                  // a workstream mid-interview. See `showConfirmDesign` in
                  // HomePage for the floor and the deferred wire-field fix.
                  <div className={confirmRow}>
                    <Button size="sm" variant="softGreen" onClick={onConfirmDesign}>
                      Looks right — set it up
                    </Button>
                  </div>
                )}
                {sendError && (
                  <div role="alert" aria-live="assertive" style={{ paddingInline: theme.space[4] }}>
                    <Callout tone="danger">{sendError}</Callout>
                  </div>
                )}
                {onRemoveFile && (
                  <AttachmentChips
                    files={stagedFiles}
                    onRemove={onRemoveFile}
                    uploadingName={uploadingFilename}
                    uploadProgress={uploadProgress}
                  />
                )}
              </div>
            )}
            <Composer
              ref={composerRef}
              value={value}
              onChange={onChange}
              onSend={onSend}
              sendDisabled={sendDisabled}
              prominence="spotlight"
              placeholder="Click here to begin…"
              leadingActions={composerLeadingActions}
            />
            {maxLength != null && (value?.length ?? 0) > maxLength && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">
                  Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                  {maxLength.toLocaleString()} characters. Shorten it to send.
                </Callout>
              </div>
            )}
            {attachError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <AttachErrorAlert
                  message={attachError}
                  onDismiss={() => {
                    onDismissAttachError();
                    // Focus would otherwise fall to <body> when the ✕ unmounts.
                    focusComposer();
                  }}
                />
              </div>
            )}
            {createError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">{createError}</Callout>
              </div>
            )}
            <div className={buttonRow}>
              {/* Same label at every stage. Live from the first user turn, so
                  it IS the escape hatch — but it creates a workstream, it does
                  not build one, so it is not labelled as an exit (ADR-0059). */}
              <Button variant="primary" disabled={createDisabled || creating} onClick={onCreate}>
                {creating ? 'Creating…' : 'Create workstream'}
              </Button>
            </div>
          </>
        }
      />
    </Modal>
  );
}
CreateWorkstreamPanel.displayName = 'CreateWorkstreamPanel';
